import { useQuery } from '@tanstack/react-query';
import { ImageOff } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useAuth } from '../auth/AuthProvider';

/** Hover this long before capturing, so sweeping the mouse over a list costs nothing. */
const OPEN_DELAY_MS = 350;
/** Card size: 320 px wide, 16:9 picture plus the caption line. */
const CARD_W = 320;
const CARD_H = 180 + 30;
const GAP = 8;

class SnapshotUnavailable extends Error {}

interface Frame {
  /**
   * The JPEG as a data URL. Frames are small, and unlike object URLs a data
   * URL needs no revoking: a cached frame stays valid across remounts (object
   * URLs were revoked by an unmount while the cache kept handing them out).
   */
  src: string;
  takenAt: string | null;
}

function toDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      if (typeof r.result === 'string') resolve(r.result);
      else reject(new Error('unreadable image'));
    };
    r.onerror = () => {
      reject(r.error ?? new Error('unreadable image'));
    };
    r.readAsDataURL(blob);
  });
}

interface Place {
  top: number;
  left: number;
}

const timeFormat = new Intl.DateTimeFormat('pt-BR', { timeStyle: 'medium' });

/** Above the trigger when it fits, below otherwise; always inside the window. */
function placeFor(r: DOMRect): Place {
  const above = r.top - GAP - CARD_H;
  const top = above >= GAP ? above : Math.min(r.bottom + GAP, window.innerHeight - CARD_H - GAP);
  const left = Math.min(Math.max(r.left, GAP), window.innerWidth - CARD_W - GAP);
  return { top, left };
}

/**
 * Shows a still frame of the camera while the pointer rests on (or keyboard
 * focus is on) its name, so the operator can tell which camera it is. The
 * card is rendered on top of the page (a portal), so scrolling containers such
 * as tables never clip it. The API captures from the lightest profile and
 * shares each frame for a minute.
 */
export function SnapshotPreview({
  deviceId,
  name,
  children,
}: {
  deviceId: string;
  name: string;
  children: ReactNode;
}) {
  const { accessToken } = useAuth();
  const [place, setPlace] = useState<Place | null>(null);
  const trigger = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const tipId = useId();
  const open = place !== null;

  const frame = useQuery({
    queryKey: ['snapshot', deviceId],
    enabled: open,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: false,
    queryFn: async ({ signal }): Promise<Frame> => {
      const token = accessToken();
      const res = await fetch(`/api/v1/devices/${encodeURIComponent(deviceId)}/snapshot`, {
        signal,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.status === 503) throw new SnapshotUnavailable();
      if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
      return { src: await toDataUrl(await res.blob()), takenAt: res.headers.get('Last-Modified') };
    },
  });

  const src = frame.data?.src;
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
    },
    [],
  );

  const hide = () => {
    window.clearTimeout(timer.current);
    setPlace(null);
  };
  const show = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const r = trigger.current?.getBoundingClientRect();
      if (r) setPlace(placeFor(r));
    }, OPEN_DELAY_MS);
  };

  // A fixed card would drift away from its row: close it when anything scrolls.
  useEffect(() => {
    if (!open) return;
    const close = () => {
      setPlace(null);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <span
      ref={trigger}
      className="inline-block"
      onPointerEnter={show}
      onPointerLeave={hide}
      onFocus={show}
      onBlur={hide}
      onKeyDown={(e) => {
        if (e.key === 'Escape') hide();
      }}
      aria-describedby={open ? tipId : undefined}
    >
      {children}
      {place &&
        createPortal(
          <span
            id={tipId}
            role="tooltip"
            style={{ top: place.top, left: place.left, width: CARD_W }}
            className="pointer-events-none fixed z-50 block overflow-hidden rounded-lg border border-line-strong bg-surface-raised shadow-xl shadow-black/40"
          >
            <span className="relative block aspect-video bg-black">
              {src ? (
                <img src={src} alt={`Imagem atual de ${name}`} className="size-full object-cover" />
              ) : frame.isError ? (
                <span className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-4 text-center text-xs text-white/70">
                  <ImageOff aria-hidden="true" className="size-5" />
                  {frame.error instanceof SnapshotUnavailable
                    ? 'A câmera não enviou imagem agora.'
                    : 'Não foi possível obter a imagem.'}
                </span>
              ) : (
                <span className="absolute inset-0 flex items-center justify-center text-xs text-white/60">
                  <span className="mr-2 size-3 animate-ping rounded-full bg-white/40" />
                  Capturando imagem…
                </span>
              )}
            </span>
            <span className="flex items-center justify-between gap-2 px-3 py-1.5 text-2xs">
              <span className="truncate font-medium text-fg">{name}</span>
              {frame.data?.takenAt && (
                <span className="shrink-0 text-fg-subtle">
                  {timeFormat.format(new Date(frame.data.takenAt))}
                </span>
              )}
            </span>
          </span>,
          document.body,
        )}
    </span>
  );
}
